'use client';

import { useMemo, useState } from 'react';
import { DatePicker } from './components/date-picker';
import { GlobalFilter } from './components/global-filter';
import { Pagination } from './components/pagination';
import { SortableHeader } from './components/sortable-header';
import { useLlmDataContext } from './context/llm-data-context';
import type { LlmEvaluationData } from './hooks/mock-llm-data';
import { fuzzySearch, paginateData } from './utils/search';
import { sortData, useSorting } from './utils/sorting';

export const LLMEvaluations = () => {
  const { evaluations, loading } = useLlmDataContext();
  const [selectedEvaluation, setSelectedEvaluation] = useState<LlmEvaluationData | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [globalFilter, setGlobalFilter] = useState('');
  const [snapshotDate, setSnapshotDate] = useState('2024-01-15');

  const { sort, handleSort } = useSorting({
    key: 'createdAt',
    direction: 'desc',
  });

  // Search fields for fuzzy search
  const searchFields = useMemo(
    () => [
      'evaluatorModel',
      'status',
      'scores.spanId',
      'scores.operationType',
      'scores.brandCode',
      'scores.traceId',
      'scores.functionId',
    ],
    [],
  );

  // Filter and paginate data
  const { filteredData, paginatedData, totalPages } = useMemo(() => {
    // Return empty data if still loading or no evaluations
    if (loading || !evaluations.length) {
      return {
        filteredData: [],
        paginatedData: [],
        totalPages: 0,
      };
    }

    // Filter by snapshot date
    const dateFiltered = evaluations.filter((evaluation) => {
      const evalDate = new Date(evaluation.createdAt).toISOString().split('T')[0];
      return evalDate === snapshotDate;
    });

    // Apply global filter
    let filtered = fuzzySearch(dateFiltered, globalFilter, searchFields);

    // Apply sorting
    if (sort) {
      filtered = sortData(filtered, sort.key, sort.direction);
    }

    const totalPages = Math.ceil(filtered.length / pageSize);
    const paginated = paginateData(filtered, currentPage, pageSize);

    return {
      filteredData: filtered,
      paginatedData: paginated,
      totalPages,
    };
  }, [loading, evaluations, globalFilter, snapshotDate, currentPage, pageSize, sort, searchFields]);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 8) return 'text-green-600';
    if (score >= 6) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className='space-y-4'>
      <div className='flex justify-between items-center'>
        <h2 className='text-xl font-semibold'>LLM Evaluations</h2>
        <div className='text-sm text-gray-500'>{filteredData.length} evaluations</div>
      </div>

      {/* Toolbar */}
      <div className='bg-white p-4 rounded-lg shadow space-y-4'>
        <div className='flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between'>
          <DatePicker value={snapshotDate} onChange={setSnapshotDate} />
          <div className='w-full sm:w-80'>
            <GlobalFilter
              value={globalFilter}
              onChange={setGlobalFilter}
              placeholder='Search evaluations...'
            />
          </div>
        </div>
      </div>

      {/* Table */}
      <div className='bg-white rounded-lg shadow overflow-hidden'>
        <div className='overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50 sticky top-0 z-10'>
              <tr>
                <SortableHeader sortKey='createdAt' currentSort={sort} onSort={handleSort}>
                  Created
                </SortableHeader>
                <SortableHeader sortKey='evaluatorModel' currentSort={sort} onSort={handleSort}>
                  Evaluator Model
                </SortableHeader>
                <SortableHeader sortKey='status' currentSort={sort} onSort={handleSort}>
                  Status
                </SortableHeader>
                <SortableHeader sortKey='scores.spanId' currentSort={sort} onSort={handleSort}>
                  Span ID
                </SortableHeader>
                <SortableHeader
                  sortKey='scores.operationType'
                  currentSort={sort}
                  onSort={handleSort}
                >
                  Operation
                </SortableHeader>
                <SortableHeader sortKey='scores.brandCode' currentSort={sort} onSort={handleSort}>
                  Brand Code
                </SortableHeader>
                <SortableHeader sortKey='scores.traceId' currentSort={sort} onSort={handleSort}>
                  Trace ID
                </SortableHeader>
                <SortableHeader sortKey='scores.functionId' currentSort={sort} onSort={handleSort}>
                  Function
                </SortableHeader>
                <SortableHeader
                  sortKey='evaluationOutput.overallScore'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Overall Score
                </SortableHeader>
                <SortableHeader
                  sortKey='latencyMs'
                  currentSort={sort}
                  onSort={handleSort}
                  align='right'
                >
                  Latency (ms)
                </SortableHeader>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {paginatedData.map((evaluation) => (
                <tr
                  key={evaluation.id}
                  className='hover:bg-gray-50 cursor-pointer'
                  onClick={() => setSelectedEvaluation(evaluation)}
                >
                  <td className='px-4 py-2 text-sm'>{formatDate(evaluation.createdAt)}</td>
                  <td className='px-4 py-2 text-sm'>{evaluation.evaluatorModel}</td>
                  <td className='px-4 py-2'>
                    <span
                      className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                        evaluation.status,
                      )}`}
                    >
                      {evaluation.status}
                    </span>
                  </td>
                  <td className='px-4 py-2 font-mono text-xs'>{evaluation.scores.spanId}</td>
                  <td className='px-4 py-2 text-sm'>{evaluation.scores.operationType}</td>
                  <td className='px-4 py-2 text-sm'>{evaluation.scores.brandCode}</td>
                  <td className='px-4 py-2 font-mono text-xs'>{evaluation.scores.traceId}</td>
                  <td className='px-4 py-2 text-sm'>{evaluation.scores.functionId}</td>
                  <td className='px-4 py-2 text-sm text-right'>
                    {evaluation.evaluationOutput?.overallScore ? (
                      <span
                        className={`font-medium ${getScoreColor(
                          evaluation.evaluationOutput.overallScore,
                        )}`}
                      >
                        {evaluation.evaluationOutput.overallScore.toFixed(1)}
                      </span>
                    ) : (
                      <span className='text-gray-400'>-</span>
                    )}
                  </td>
                  <td className='px-4 py-2 text-sm text-right'>
                    {evaluation.latencyMs || <span className='text-gray-400'>-</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={filteredData.length}
          onPageChange={setCurrentPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />
      </div>

      {/* Detail Panel */}
      {selectedEvaluation && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white rounded-lg p-6 max-w-4xl max-h-[80vh] overflow-y-auto w-full mx-4'>
            <div className='flex justify-between items-center mb-4'>
              <h3 className='text-lg font-semibold'>Evaluation Details</h3>
              <button
                type='button'
                onClick={() => setSelectedEvaluation(null)}
                className='text-gray-500 hover:text-gray-700'
              >
                ✕
              </button>
            </div>

            <div className='space-y-4'>
              <div>
                <h4 className='font-medium text-gray-700 mb-2'>Evaluation Prompt</h4>
                <div className='bg-gray-100 p-3 rounded text-sm'>
                  {selectedEvaluation.evaluationPrompt}
                </div>
              </div>

              {selectedEvaluation.evaluationOutput && (
                <>
                  <div>
                    <h4 className='font-medium text-gray-700 mb-2'>Summary</h4>
                    <div className='bg-blue-50 p-3 rounded text-sm'>
                      {selectedEvaluation.evaluationOutput.summary}
                    </div>
                  </div>

                  <div>
                    <h4 className='font-medium text-gray-700 mb-2'>Score Categories</h4>
                    <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                      {Object.entries(selectedEvaluation.evaluationOutput)
                        .filter(
                          ([_key, value]) =>
                            typeof value === 'object' &&
                            value !== null &&
                            'score' in value &&
                            'reasoning' in value,
                        )
                        .map(([category, details]) => (
                          <div key={category} className='bg-gray-50 p-3 rounded'>
                            <div className='flex justify-between items-center mb-2'>
                              <span className='font-medium capitalize'>
                                {category.replace(/([A-Z])/g, ' $1').trim()}
                              </span>
                              <span
                                className={`font-bold ${getScoreColor(
                                  (
                                    details as {
                                      score: number;
                                      reasoning: string;
                                    }
                                  ).score,
                                )}`}
                              >
                                {
                                  (
                                    details as {
                                      score: number;
                                      reasoning: string;
                                    }
                                  ).score
                                }
                                /10
                              </span>
                            </div>
                            <p className='text-sm text-gray-600'>
                              {
                                (
                                  details as {
                                    score: number;
                                    reasoning: string;
                                  }
                                ).reasoning
                              }
                            </p>
                          </div>
                        ))}
                    </div>
                  </div>

                  {selectedEvaluation.evaluationOutput.improvements && (
                    <div>
                      <h4 className='font-medium text-gray-700 mb-2'>Improvements</h4>
                      <ul className='bg-yellow-50 p-3 rounded text-sm space-y-1'>
                        {selectedEvaluation.evaluationOutput.improvements.map(
                          (improvement: string, index: number) => (
                            <li key={index} className='flex items-start'>
                              <span className='text-yellow-600 mr-2'>•</span>
                              {improvement}
                            </li>
                          ),
                        )}
                      </ul>
                    </div>
                  )}

                  <div>
                    <h4 className='font-medium text-gray-700 mb-2'>Full Evaluation Output</h4>
                    <pre className='bg-gray-100 p-3 rounded text-sm overflow-x-auto'>
                      {JSON.stringify(selectedEvaluation.evaluationOutput, null, 2)}
                    </pre>
                  </div>
                </>
              )}

              {!selectedEvaluation.evaluationOutput && (
                <div className='bg-yellow-50 p-4 rounded'>
                  <p className='text-yellow-800'>This evaluation is still pending completion.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
